import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const faqs = [
  {
    q: 'What is the difference between agentic AI and ChatGPT?',
    a: 'ChatGPT answers questions — you type something, it responds, and then it waits for your next message. Agentic AI takes action. You give it an objective ("follow up with every lead from last week") and it breaks that into steps, uses your tools, makes decisions along the way, and completes the work. The difference is the same as asking a colleague a question versus assigning them a task.',
  },
  {
    q: 'Do I need an IT department or technical staff to use agentic AI?',
    a: "No. Modern agentic AI tools are configured in plain English, not code. You describe the agent's job the way you'd brief a new hire — its role, what it should and shouldn't do, and what tools it can use. For anything more complex, that's what consultants like Sagient Partners are for.",
  },
  {
    q: 'What tasks can agentic AI automate for a small business?',
    a: 'The best candidates are multi-step, rules-governed tasks that repeat regularly: responding to and routing inbound leads, drafting and sending follow-up emails, generating invoices and chasing payments, scheduling appointments, pulling weekly reports from multiple systems, and onboarding new clients or volunteers. If a task has a consistent pattern, it can likely be automated.',
  },
  {
    q: 'What can agentic AI do for a nonprofit?',
    a: 'Nonprofits are using agentic AI to screen grant opportunities against their eligibility criteria, manage donor communication workflows, prep board meeting materials, route volunteer inquiries, and track program outcomes. It replaces the manual coordination work that consumes program staff time.',
  },
  {
    q: 'Is agentic AI safe for my business data?',
    a: 'Yes, when configured correctly. Business-tier agentic AI tools do not train on your data. The key is setting written guardrails upfront — for example, "never send an email automatically, always save as a draft for human review." For regulated information (health records, financial data), you use only vendors who will sign the appropriate agreements. A properly set-up agent is no riskier than giving a trusted employee access to your systems.',
  },
  {
    q: 'How is agentic AI different from regular automation or software?',
    a: 'Traditional automation (like a Zapier trigger) follows a fixed script: if X happens, do Y. It breaks the moment something unexpected occurs. Agentic AI reasons through exceptions — it can read a confusing email, determine the right action, draft a reply in your voice, and flag it for human review rather than failing silently. That gap — handling the messy middle — is what makes it transformative for small organizations.',
  },
  {
    q: 'How much does agentic AI cost to implement?',
    a: 'The AI tools themselves are typically $20–$200 per month depending on usage — far less than a part-time hire. The larger investment is the setup: understanding your workflows, configuring the agents correctly, and testing them before they run live. A Sagient Partners engagement starts at $3,000 for an AI Readiness Assessment, which maps your workflows and identifies where the returns are highest before you spend anything on implementation.',
  },
  {
    q: 'What is the main difference between Generative AI and Agentic AI?',
    a: 'Generative AI (like ChatGPT) produces content — text, images, code — in response to a prompt. It waits for your next input. Agentic AI pursues a goal: it breaks the goal into steps, uses external tools and software, makes decisions when it encounters obstacles, and completes multi-step workflows without constant human direction.',
  },
  {
    q: 'How do autonomous AI agents use digital tools?',
    a: 'Autonomous AI agents are connected to external tools via APIs and integrations — your email inbox, CRM, spreadsheets, calendar, databases, and web browsers. When given a task, the agent selects the right tools, executes the steps in the correct sequence, reads the results, and adjusts its approach if something unexpected happens.',
  },
  {
    q: 'Is Agentic AI safe for small business workflows?',
    a: 'Yes, when set up with proper guardrails. The key practices are: (1) using business-tier AI tools that do not train on your data, (2) setting explicit boundaries in plain English (e.g., "never send anything automatically"), (3) running every new workflow through a human-in-the-loop pilot before it goes live. A well-configured agentic AI workflow is no riskier than giving a trusted employee access to your systems.',
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid #E2E8F0' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: '20px 0', textAlign: 'left', gap: 16 }}
        aria-expanded={open}
      >
        <span style={{ fontWeight: 700, color: '#0A1628', fontSize: '0.97rem' }}>{q}</span>
        <span style={{ color: '#378ADD', fontSize: '1.2rem', fontWeight: 700, flexShrink: 0 }}>{open ? '−' : '+'}</span>
      </button>
      {open && <p style={{ fontSize: '0.97rem', color: '#64748B', lineHeight: 1.75, paddingBottom: 20, margin: 0 }}>{a}</p>}
    </div>
  );
}

const characteristics = [
  {
    name: '1. Autonomy: Executing Multi-Step Goals',
    desc: 'An agentic AI breaks a broad goal into a sequence of tasks and carries them out without asking for confirmation at every step. You set the objective and the guardrails; it figures out the steps.',
    icon: '🤖',
  },
  {
    name: '2. Tool Use: Connecting APIs and Business Apps',
    desc: (
      <>
        Unlike a chatbot that only talks, an agentic AI can interact with the outside world — browsing the web, reading your inbox, writing to a spreadsheet, calling your CRM, or sending a message. It{' '}
        <Link to="/services/ai-strategy-implementation" style={{ color: '#378ADD', fontWeight: 600 }}>orchestrates workflows across multiple apps and systems</Link>,
        using your existing software as its hands.
      </>
    ),
    icon: '🔧',
  },
  {
    name: '3. Reasoning & Planning: Overcoming Process Roadblocks',
    desc: "When something unexpected happens — a vague email, a missing field, an error — an agentic AI evaluates the situation, considers its options, and decides what to do next. It doesn't just fail; it adapts.",
    icon: '🧠',
  },
  {
    name: '4. Memory: Long-Term Context and Preference Retention',
    desc: 'Agentic AI can hold context within a task (what it has done so far) and across tasks (your preferences, past results, ongoing relationships). Over time it builds an operational picture of how your business works.',
    icon: '💾',
  },
];

const comparisonRows = [
  { label: 'What you provide', gen: 'A prompt or question', agentic: 'A goal or outcome' },
  { label: 'Workflow Execution', gen: 'Generates a response', agentic: 'Plans and executes steps to complete the task' },
  { label: 'Human Involvement', gen: 'Required for every action', agentic: 'Set guardrails upfront; review exceptions' },
  { label: 'Integration & Software Scope', gen: 'One response at a time', agentic: 'Multi-step workflows across tools and systems' },
  { label: 'Example', gen: '"Draft a follow-up email for this lead"', agentic: '"Follow up with every open lead from last week and flag any that reply"' },
];

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'TechArticle',
      headline: 'What is Agentic AI? Definition, Features & Examples',
      description: 'Agentic AI refers to autonomous AI systems that complete complex, multi-step goals without step-by-step human direction. Learn how it differs from generative AI and what it means for small businesses and nonprofits.',
      url: 'https://sagientpartners.com/agentic-ai',
      author: { '@type': 'Organization', name: 'Sagient Partners', url: 'https://sagientpartners.com' },
      publisher: {
        '@type': 'Organization',
        name: 'Sagient Partners',
        url: 'https://sagientpartners.com',
        logo: { '@type': 'ImageObject', url: 'https://sagientpartners.com/social_card.png' },
      },
      datePublished: '2025-07-01',
      dateModified: '2025-07-01',
      about: {
        '@type': 'DefinedTerm',
        name: 'Agentic AI',
        description: 'Autonomous artificial intelligence systems designed to achieve complex, multi-step goals without step-by-step human intervention by planning tasks, using external digital tools, and executing workflows end-to-end.',
        inDefinedTermSet: 'https://sagientpartners.com/agentic-ai',
      },
    },
    {
      '@type': 'FAQPage',
      mainEntity: faqs.map(f => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: typeof f.a === 'string' ? f.a : f.q },
      })),
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://sagientpartners.com' },
        { '@type': 'ListItem', position: 2, name: 'What Is Agentic AI?', item: 'https://sagientpartners.com/agentic-ai' },
      ],
    },
  ],
};

const tdBase = { padding: '14px 16px', fontSize: '0.9rem', verticalAlign: 'top' };
const thBase = { ...tdBase, fontWeight: 700, background: '#0C447C', color: '#fff', textAlign: 'left' };
const tdLeft = { ...tdBase, fontWeight: 600, color: '#0A1628', background: '#F8FAFC', width: '28%' };

export default function AgenticAI() {
  return (
    <>
      <Helmet>
        <title>What is Agentic AI? Definition, Features &amp; Examples | Sagient Partners</title>
        <meta name="description" content="Agentic AI refers to autonomous AI systems that complete complex, multi-step goals without step-by-step human direction. Learn how it differs from generative AI and what it means for your business." />
        <link rel="canonical" href="https://sagientpartners.com/agentic-ai" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sagientpartners.com/agentic-ai" />
        <meta property="og:title" content="What is Agentic AI? Definition, Features & Examples | Sagient Partners" />
        <meta property="og:description" content="Agentic AI refers to autonomous AI systems that complete complex, multi-step goals without step-by-step human direction. Learn how it differs from generative AI." />
        <meta property="og:image" content="https://sagientpartners.com/social_card.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="What is Agentic AI? Definition, Features & Examples | Sagient Partners" />
        <meta name="twitter:description" content="Agentic AI refers to autonomous AI systems that complete complex, multi-step goals without step-by-step human direction. Learn how it differs from generative AI." />
        <meta name="twitter:image" content="https://sagientpartners.com/social_card.png" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* Hero */}
      <section style={{ background: 'linear-gradient(135deg, #0C447C 0%, #0A1628 100%)', padding: '40px 0 48px', color: '#fff' }}>
        <div className="container">
          <nav style={{ fontSize: '0.82rem', color: '#B5D4F4', marginBottom: 20 }} aria-label="Breadcrumb">
            <Link to="/" style={{ color: '#B5D4F4', textDecoration: 'none' }}>Home</Link>
            <span style={{ margin: '0 8px' }}>›</span>
            <span>What Is Agentic AI?</span>
          </nav>
          <p className="section-label" style={{ color: '#B5D4F4' }}>AI Explained</p>
          <h1 style={{ color: '#fff', maxWidth: 720, marginBottom: 20 }}>What is Agentic AI? Agentic AI vs. Generative AI Explained</h1>
          <p style={{ color: '#B5D4F4', fontSize: '1.1rem', maxWidth: 620, lineHeight: 1.7 }}>
            A plain-language guide for small business owners and nonprofit leaders who want to understand what agentic AI actually is — and whether it can help their organization.
          </p>
        </div>
      </section>

      {/* Featured snippet + Short Answer */}
      <section className="section" style={{ paddingTop: 56, paddingBottom: 48 }}>
        <div className="container" style={{ maxWidth: 760 }}>
          <p className="section-label">The Short Answer</p>
          <h2 className="section-heading">AI That Acts, Not Just Answers</h2>

          {/* 40–50 word definition — targets "What is..." featured snippets */}
          <p style={{ fontSize: '1.1rem', color: '#0A1628', lineHeight: 1.8, marginBottom: 28, padding: '20px 24px', background: '#F8FAFC', borderLeft: '4px solid #378ADD', borderRadius: 6 }}>
            <strong>Agentic AI</strong> refers to autonomous artificial intelligence systems designed to achieve complex, multi-step goals without step-by-step human intervention. Unlike <strong>Generative AI</strong>, which creates text or media based on static prompts, Agentic AI plans tasks, uses external digital tools, and executes workflows end-to-end.
          </p>

          <p style={{ fontSize: '1rem', color: '#475569', lineHeight: 1.8, marginBottom: 16 }}>
            Agentic AI is an AI system that can take action to complete a goal — not just respond to a question. You give it an objective, and it plans the steps, uses your tools and systems, makes decisions along the way, and gets the work done with minimal hand-holding.
          </p>
          <p style={{ fontSize: '1rem', color: '#475569', lineHeight: 1.8 }}>
            The term "agentic" comes from <em>agency</em> — the capacity to act independently. An agentic AI has agency. It doesn't wait for your next message. It works.
          </p>
        </div>
      </section>

      {/* Analogy */}
      <section className="section section--alt" style={{ paddingTop: 48, paddingBottom: 48 }}>
        <div className="container" style={{ maxWidth: 760 }}>
          <p className="section-label">The Analogy That Makes It Click</p>
          <h2 className="section-heading">Agentic AI vs. Generative AI: From Assistant to Delegate</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 8 }}>
            <div style={{ background: '#fff', borderRadius: 10, padding: '28px 24px', border: '1px solid #E2E8F0' }}>
              <p style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748B', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Traditional AI (ChatGPT)</p>
              <p style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0A1628', marginBottom: 10 }}>Like a calculator</p>
              <p style={{ fontSize: '0.93rem', color: '#475569', lineHeight: 1.7 }}>
                You give <strong>Generative AI tools</strong> a specific input and get a specific output. "Draft a reply to this email" → you get a draft. Then it waits. One task, done.
              </p>
            </div>
            <div style={{ background: '#EBF4FD', borderRadius: 10, padding: '28px 24px', border: '1px solid #B5D4F4' }}>
              <p style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0C447C', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Agentic AI</p>
              <p style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0A1628', marginBottom: 10 }}>Like a capable delegate</p>
              <p style={{ fontSize: '0.93rem', color: '#475569', lineHeight: 1.7 }}>
                <strong>Autonomous AI delegates</strong> pursue an objective. "Follow up with every lead from last week, send a personalized email, and flag anyone who replies." It figures out the steps and does the work.
              </p>
            </div>
          </div>
          <p style={{ fontSize: '0.97rem', color: '#475569', lineHeight: 1.75, marginTop: 24 }}>
            With a calculator, you are still doing the work — just faster. With a delegate, you shift from doing to supervising. Agentic AI moves you into the{' '}
            <Link to="/services/ai-readiness-assessment" style={{ color: '#378ADD', fontWeight: 600 }}>supervisor role</Link>.
            That shift is where the real productivity gain lives.
          </p>
        </div>
      </section>

      {/* Four characteristics */}
      <section className="section section--alt" style={{ paddingTop: 48, paddingBottom: 48 }}>
        <div className="container">
          <p className="section-label">How It Works</p>
          <h2 className="section-heading" style={{ marginBottom: 32 }}>4 Core Characteristics of Agentic AI Workflows</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            {characteristics.map(c => (
              <div key={c.name} style={{ background: '#fff', borderRadius: 10, padding: '28px 24px', border: '1px solid #E2E8F0' }}>
                <div style={{ fontSize: '2rem', marginBottom: 12 }}>{c.icon}</div>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#0A1628', marginBottom: 10 }}>{c.name}</h3>
                <p style={{ fontSize: '0.92rem', color: '#475569', lineHeight: 1.7, margin: 0 }}>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="section" style={{ paddingTop: 48, paddingBottom: 48 }}>
        <div className="container" style={{ maxWidth: 760 }}>
          <p className="section-label">Agentic AI vs. ChatGPT</p>
          <h2 className="section-heading">Key Differences: Generative AI vs. Agentic AI Comparison</h2>
          <p style={{ fontSize: '1rem', color: '#475569', lineHeight: 1.8, marginBottom: 32 }}>
            Most people's mental model of AI is ChatGPT: you type a question, you get an answer, you type again. That's <strong>generative AI</strong> — it generates content on demand. Agentic AI is a different category. Here's the comparison:
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: 8, overflow: 'hidden', border: '1px solid #E2E8F0' }}>
              <thead>
                <tr>
                  <th style={thBase}> </th>
                  <th style={thBase}>Generative AI (ChatGPT)</th>
                  <th style={{ ...thBase, background: '#0A1628' }}>Agentic AI</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={row.label} style={{ background: i % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                    <td style={tdLeft}>{row.label}</td>
                    <td style={{ ...tdBase, color: '#475569' }}>{row.gen}</td>
                    <td style={{ ...tdBase, color: '#0C447C', fontWeight: 500 }}>{row.agentic}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: '0.9rem', color: '#64748B', marginTop: 16, fontStyle: 'italic' }}>
            Generative AI helps you think and draft. Agentic AI helps you execute and operate.
          </p>
        </div>
      </section>

      {/* Real-world examples */}
      <section className="section section--alt" style={{ paddingTop: 48, paddingBottom: 48 }}>
        <div className="container" style={{ maxWidth: 760 }}>
          <p className="section-label">Real-World Examples</p>
          <h2 className="section-heading">What Agentic AI Actually Does for Small Organizations</h2>
          <p style={{ fontSize: '1rem', color: '#475569', lineHeight: 1.8, marginBottom: 32 }}>
            This is not a technology looking for a use case. These are workflows that most small businesses and nonprofits deal with every week — and that agentic AI can take off your team's plate.
          </p>

          <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0C447C', marginBottom: 16 }}>For Small Businesses</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 40 }}>
            {[
              { title: 'Lead follow-up that actually happens', desc: 'An agent monitors your inbox for new inquiries, scores each lead, sends a personalized reply within minutes, and adds a task to your CRM if the lead looks hot. No lead falls through the cracks because someone was in a meeting.' },
              { title: 'Invoicing and accounts receivable', desc: 'An agent generates invoices from completed jobs, matches incoming payments, and sends polite, escalating follow-ups when payments are overdue — without you drafting each email.' },
              { title: 'Quote and proposal drafting', desc: 'Pull your pricing, past proposals, and client context together. An agent drafts a proposal in your format, ready for your review and a single click to send.' },
              { title: 'Weekly reporting', desc: 'Pull numbers from your CRM, accounting software, and calendar. An agent assembles a weekly operations summary and sends it to your inbox every Monday morning.' },
            ].map(ex => (
              <div key={ex.title} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <span style={{ color: '#378ADD', fontWeight: 700, fontSize: '1.1rem', marginTop: 2, flexShrink: 0 }}>✓</span>
                <div>
                  <p style={{ fontWeight: 700, color: '#0A1628', marginBottom: 4, fontSize: '0.97rem' }}>{ex.title}</p>
                  <p style={{ color: '#475569', fontSize: '0.92rem', lineHeight: 1.7, margin: 0 }}>{ex.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0C447C', marginBottom: 16 }}>For Nonprofits</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { title: 'Grant vetting and opportunity matching', desc: "An agent reads new grant RFPs, parses the eligibility requirements, and scores each one against your organization's profile and priorities — so you only spend time on grants you can actually win." },
              { title: 'Donor stewardship workflows', desc: 'Segment donors by giving history and interests. An agent drafts personalized update emails, acknowledgment letters, and renewal asks — keeping your donor relationships warm without the manual effort.' },
              { title: 'Volunteer coordination', desc: "Route volunteer inquiries to the right program coordinator, send onboarding sequences, and follow up automatically with people who applied but haven't completed their training." },
              { title: 'Board meeting preparation', desc: 'An agent pulls financials, program reports, and committee updates from your existing documents and assembles a draft board pack, ready for your final review.' },
            ].map(ex => (
              <div key={ex.title} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <span style={{ color: '#378ADD', fontWeight: 700, fontSize: '1.1rem', marginTop: 2, flexShrink: 0 }}>✓</span>
                <div>
                  <p style={{ fontWeight: 700, color: '#0A1628', marginBottom: 4, fontSize: '0.97rem' }}>{ex.title}</p>
                  <p style={{ color: '#475569', fontSize: '0.92rem', lineHeight: 1.7, margin: 0 }}>{ex.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Readiness / myth-busting */}
      <section className="section" style={{ paddingTop: 48, paddingBottom: 48 }}>
        <div className="container" style={{ maxWidth: 760 }}>
          <p className="section-label">A Common Misconception</p>
          <h2 className="section-heading">"We're Not Ready for AI Yet"</h2>
          <p style={{ fontSize: '1rem', color: '#475569', lineHeight: 1.8, marginBottom: 20 }}>
            Most small organizations assume they're not ready for AI — they think they need perfect data, a modern tech stack, or a dedicated IT person first. That's almost never true.
          </p>
          <p style={{ fontSize: '1rem', color: '#475569', lineHeight: 1.8, marginBottom: 20 }}>
            Readiness isn't about your technology. It's about having a real problem worth solving and the willingness to change a process. Organizations have automated meaningful workflows with little more than a spreadsheet, an inbox, and the right tools connected together.
          </p>
          <p style={{ fontSize: '1rem', color: '#475569', lineHeight: 1.8, marginBottom: 24 }}>
            What matters is identifying which workflows are worth automating first — where the hours are actually going, and where getting those hours back would change what your team can do. That's exactly what a structured{' '}
            <Link to="/services/ai-readiness-assessment" style={{ color: '#378ADD', fontWeight: 600 }}>AI Readiness Assessment</Link> surfaces.
            After implementation, ongoing management through a{' '}
            <Link to="/services/ai-advisor" style={{ color: '#378ADD', fontWeight: 600 }}>Fractional AI Advisor</Link>{' '}
            ensures your automation footprint keeps growing.
          </p>
          <div style={{ background: '#EBF4FD', borderRadius: 10, padding: '24px 28px', borderLeft: '4px solid #378ADD' }}>
            <p style={{ fontSize: '0.97rem', color: '#0C447C', lineHeight: 1.7, margin: 0, fontStyle: 'italic' }}>
              75% of small businesses are already at least experimenting with AI. Among those who have adopted it, 91% report that it boosts revenue and 86% see better margins. But most of that is still one person prompting ChatGPT, one draft at a time. The organizations seeing the biggest results are the ones turning AI into documented, repeatable workflows.
            </p>
          </div>
        </div>
      </section>

      {/* CTA before FAQ */}
      <section style={{ background: '#0C447C', padding: '40px 0', textAlign: 'center' }}>
        <div className="container">
          <p style={{ color: '#B5D4F4', fontSize: '1rem', marginBottom: 16, marginTop: 0 }}>
            Want to know where agentic AI would have the most impact in your organization?
          </p>
          <Link to="/contact" className="btn btn-primary" style={{ fontSize: '1rem' }}>Book a Free Discovery Call →</Link>
          <p style={{ color: '#B5D4F4', fontSize: '0.85rem', marginTop: 16, marginBottom: 0 }}>
            30 minutes. No jargon. We'll tell you honestly whether AI makes sense for your situation.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="section" style={{ paddingTop: 56, paddingBottom: 56 }}>
        <div className="container" style={{ maxWidth: 760 }}>
          <p className="section-label">Common Questions</p>
          <h2 className="section-heading" style={{ marginBottom: 8 }}>Frequently Asked Questions</h2>
          <p style={{ color: '#64748B', fontSize: '0.97rem', marginBottom: 32 }}>
            Answers to the questions small business owners and nonprofit leaders ask most when learning about agentic AI.
          </p>
          <div>
            {faqs.map(f => <FaqItem key={f.q} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* Where to start */}
      <section className="section section--alt" style={{ paddingTop: 48, paddingBottom: 48 }}>
        <div className="container" style={{ maxWidth: 760 }}>
          <p className="section-label">Next Steps</p>
          <h2 className="section-heading">How to Get Started with Agentic AI</h2>
          <p style={{ fontSize: '1rem', color: '#475569', lineHeight: 1.8, marginBottom: 20 }}>
            For most small organizations, the right starting point isn't buying a tool — it's understanding where the time actually goes first. The workflows that look obvious from the outside are rarely the ones that deliver the most value when automated.
          </p>
          <p style={{ fontSize: '1rem', color: '#475569', lineHeight: 1.8, marginBottom: 32 }}>
            Sagient Partners works exclusively with small businesses and nonprofits to find, build, and{' '}
            <Link to="/services/ai-advisor" style={{ color: '#378ADD', fontWeight: 600 }}>manage agentic AI workflows</Link> on an ongoing basis.
            We start every engagement with a structured{' '}
            <Link to="/services/ai-readiness-assessment" style={{ color: '#378ADD', fontWeight: 600 }}>Readiness Assessment</Link>{' '}
            that traces your core workflows end to end, identifies the highest-value automation opportunities, and delivers a prioritized roadmap — so you know exactly what to build and in what order, before you spend anything on implementation.
          </p>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Link to="/services/ai-readiness-assessment" className="btn btn-primary">See the Readiness Assessment →</Link>
            <Link to="/services" style={{ display: 'inline-flex', alignItems: 'center', padding: '12px 24px', fontWeight: 600, color: '#0C447C', fontSize: '0.97rem' }}>View All Services →</Link>
          </div>
        </div>
      </section>
    </>
  );
}
